import React, { useContext, useState, useEffect } from "react";
import GenericModal from "./GenericModal";
import { Button, TextField } from "@mui/material";
import genericStyles from "../Styles/genericStyles.module.css";
import { Vocabulary } from "../Utils/Vocabulary";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import DropzoneComponent from "./DropzoneComponent";
import { ro } from "date-fns/locale";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SaveIcon from "@mui/icons-material/Save";
import { endpoints } from "../Utils/UrlEnum";
import { WebSocketContext } from "../Context/WebSocketContext";
import { postData } from "../Services/postData";
import { updateData } from "../Services/updateData";

interface DocumentModalProps {
  title: string;
  documentType: string; // 'psychological', 'medical', or 'criminal'
  onClose: () => void;
  control: any;
  getValues: any;
  setValue: any;
  onDocumentSaved?: () => void;
  allDocumentTypes?: any[];
  existingDocument?: any;
}

const DocumentModal: React.FC<DocumentModalProps> = ({
  title,
  documentType,
  onClose,
  getValues,
  onDocumentSaved,
  allDocumentTypes,
  existingDocument,
}) => {
  const websocketContext = useContext(WebSocketContext);
  const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    existingDocument?.expirationDate
      ? new Date(existingDocument.expirationDate)
      : null
  );
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);

  // Find document type ID by slug from passed prop
  useEffect(() => {
    if (allDocumentTypes && allDocumentTypes.length > 0) {
      const docType = allDocumentTypes.find(
        (dt: any) => dt?.name?.toLowerCase() === documentType?.toLowerCase()
      );
      if (docType) {
        setDocumentTypeId(docType._id);
      }
    }
  }, [documentType, allDocumentTypes]);

  const handleFileChange = (files: File[]) => {
    setDocumentFiles(files);
  };

  /**
   * Submits the document for upload to the server using the new document structure
   */
  const handleSubmit = async () => {
    if (!documentTypeId) {
      console.error("Document type ID not found");
      return;
    }

    const clientId = getValues("client_id._id");
    const orderId = getValues("_id");

    if (!clientId || !orderId) {
      console.error("Client ID or Order ID not found");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("documentTypeId", documentTypeId);
      formData.append("clientId", clientId);
      formData.append("orderId", orderId);

      if (expirationDate) {
        formData.append("expirationDate", expirationDate.toISOString());
      }
      if (existingDocument?._id) {
        formData.append("_id", existingDocument._id);
      }

      documentFiles.forEach((file) => formData.append("documents", file));

      const isUpdate = Boolean(existingDocument?._id);
      const url = isUpdate
        ? `${endpoints.document}/${existingDocument._id}`
        : endpoints.document;

      const apiCall = isUpdate ? updateData : postData;
      const response = await apiCall(url, formData);

      if (response?.data) {
        websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
        onDocumentSaved?.();
        onClose();
      }
    } catch (error) {
      console.error("Error saving document:", error);
    }
  };

  return (
    <GenericModal
      open={true}
      title={title}
      children={
        <div className={genericStyles.modalContent}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
            <DatePicker
              label={Vocabulary.expirationDocumentDate}
              value={expirationDate}
              onChange={(newDate) => setExpirationDate(newDate)}
              views={["month", "year", "day"]}
              slots={{ textField: TextField }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal",
                },
              }}
            />
          </LocalizationProvider>

          <DropzoneComponent
            onSave={handleFileChange}
            maxFiles={10}
            multiple={true}
            paths={
              existingDocument?.document
                ? ["/" + existingDocument.document]
                : []
            }
            isPreview
            acceptedFileTypes={{
              "application/pdf": [".pdf"],
              "image/*": [".png", ".jpg", ".jpeg"],
              "application/msword": [".doc"],
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                [".docx"],
            }}
          />
        </div>
      }
      actions={
        <div className={genericStyles.modalActions}>
          <Button color="error" variant="outlined" fullWidth onClick={onClose}>
            {Vocabulary.cancel}
          </Button>
          <Button
            fullWidth
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={
              !documentTypeId ||
              (documentFiles.length === 0 && !existingDocument)
            }
          >
            {Vocabulary.save}
          </Button>
        </div>
      }
      onClose={onClose}
    />
  );
};

export default DocumentModal;
